import { ErrorMessage, Field, FieldProps, Form, Formik } from "formik"
import { useEffect, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import * as Yup from 'yup'
import { useInvites } from "../../../hooks/community/useInvites"
import { useMastermindId } from "../../../hooks/community/useMastermindId"
import { useSuggestTags } from "../../../hooks/community/useSuggestTags"
import { LoaderOverlay } from "../../../hooks/loader/LoaderOverlay"
import { CommunityMastermindCreateModel } from "../models/CommunityMastermindCreateModel"
import { CommunityTagModel } from "../models/CommunityTagModel"
import { CommunityFacilitatorSelect } from "./CommunityFacilitatorSelect"
import { CommunityInput } from "./CommunityInput"
import { CommunityTagInput } from "./CommunityTagInput"

const inits: CommunityMastermindCreateModel = {
    topic: '',
    content: '',
    tags: [],
    invites: [],
    facilitator: null
}

const validationSchema = Yup.object({
    topic: Yup.string().required('Topic is required'),
    content: Yup.string().required('Content is required'),
    tags: Yup.array()
            .min(1, "Should contain at least one tag")
            .required("Tag is required"),
    invites: Yup.array()
            // .min(1, "Should contain at least one tag")
            .max(12)
            .notRequired(),
    facilitator: Yup.number().nullable()
});

type Props = {
    onSubmit: (value: CommunityMastermindCreateModel, id?: string) => void;
    redirectOnFailed?: string;
}

export const CommunityMastermindForm = ({ onSubmit, redirectOnFailed }: Props) => {
    const { id } = useParams() as any;
    const history = useHistory();
    const { loading, data, error } = useMastermindId(id);
    const [tagInput, setTagInput] = useState('');
    const { tags } = useSuggestTags(tagInput);
    const [invitesInput, setInvitesInput] = useState('');
    const { invites } = useInvites(invitesInput);
    const [initialValues, setInitialValues] = useState(inits);

    useEffect(() => {
        if(!!error)
            return history.replace(redirectOnFailed ?? '/community');
    }, [error]);


    useEffect(() => {
        if(!!data) {
            setInitialValues({ 
                topic: data.topic,
                content: data.content, 
                tags: (data.tags.map(t => ({ label: t, value: t }))  as CommunityTagModel[]),
                invites: (data.invites.map(i => ({ imageUrl: i.avatar, label: i.fullName, value: i }))) as CommunityTagModel[],
                facilitator: data.facilitator
            });
        }
    }, [data]);

    return (
        <>
            <LoaderOverlay visible={loading} message={'Loading...'} />
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={(f) => onSubmit(f, id)}
                validationSchema={validationSchema}
            >
                {() => (
                    <Form>
                        <div className="community-mastermind-form">
                            <div className="community-form__info">
                                <div className="community-metadata">
                                    <div>
                                        <h4>
                                            What will your circle explore?
                                        </h4>
                                        <p style={{ margin: '0', marginTop: '10px'}}>
                                            Mastermind Circles—private groups limited to 12 members—where trusted participants engage in targeted conversations, share expertise, and build meaningful connections online or in person.
                                        </p>
                                    </div>
                                    <div className="d-flex flex-column w-100" style={{ gap: '30px' }}>
                                        <div className="w-100">
                                            <Field name="topic">
                                                {({ field, form }: FieldProps) => (
                                                    <>
                                                        <CommunityInput
                                                            type="line"
                                                            title="Topic"
                                                            description="Define the focus of your circle"
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                        />
                                                        <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                                    </>
                                                )}
                                            </Field> 
                                        </div>
                                        
                                        <div className="w-100">
                                            <Field name="content"> 
                                                {({ field, form }: FieldProps) => (
                                                    <>
                                                        <CommunityInput
                                                            type="rich"
                                                            title="description"
                                                            description="Outline your goals, the expertise you seek, and what trusted members can expect to exchange in this circle."
                                                            value={field.value}
                                                            onChange={(value) => form.setFieldValue(field.name, value)}
                                                        />
                                                        <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="w-100">
                                    <Field name="tags">
                                        {({ field, form }: FieldProps) => (
                                            <>
                                                <CommunityTagInput
                                                    title="Tags"
                                                    description="Choose tags that highlight the key points."
                                                    value={field.value}
                                                    onChange={(value) => form.setFieldValue(field.name, value)}
                                                    onChangeInput={setTagInput}
                                                    suggests={tags.results.map(t => t.name)}
                                                    placeholder="Type and press Enter..."
                                                />
                                                <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                            </>
                                        )}
                                    </Field>
                                </div>
                                
                                <div className="w-100">
                                    <Field name="invites">
                                        {({ field, form }: FieldProps) => (
                                            <>
                                                <CommunityTagInput
                                                    title="Invite peers"
                                                    description="You can invite both Members and Experts to your Mastermind Circle. Invitee must accept the invitation within 7 days—otherwise, it will be considered declined. Only after a decline you can invite someone else to maintain the 12-member limit."
                                                    options={invites}
                                                    tagsClassName="community-tag-invite"
                                                    value={field.value}
                                                    onChange={(value) => {form.setFieldValue(field.name, value)}}
                                                    onChangeInput={setInvitesInput}
                                                    placeholder="Type and press Enter..."
                                                />
                                                <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                            </>
                                        )}
                                    </Field>
                                </div>

                                <div className="community-invite-wrapper">
                                    <div className="info">
                                        <span className="title">Assign Facilitator</span>
                                        <span className="description">Assign a Facilitator to lead the discussion and bring peers together for an in-person gathering.</span>
                                    </div>
                                    <Field name="facilitator">
                                        {({ field, form }: FieldProps) => (
                                            <>
                                                <CommunityFacilitatorSelect 
                                                    onAddNewTeamMember={() => {}}
                                                    onChange={(id) => form.setFieldValue(field.name, id)}
                                                    value={field.value}
                                                />
                                                <ErrorMessage name={field.name} component="div" className="tap-add-experience-text text-danger" />
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </div>
                            <button type="submit">
                                {id ? 'Save changes' : 'Start your circle'}
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                    <path d="M8.5 0.378906L12 3.38306H0" stroke="white"/>
                                </svg>
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </>
    )
}